import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import Modal from '../../components/ui/Modal';
import TenantLogo from '../../components/admin/TenantLogo';
import {
  Activity,
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  Clock,
  Eye,
  RefreshCw,
  Search,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
];

const STATUSES = [
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'paid', label: 'Paid' },
  { id: 'all', label: 'All' },
];

const RISK_INDEXES = [
  { id: 'all', label: 'All risk' },
  { id: 'low', label: 'Low' },
  { id: 'mid', label: 'Mid' },
  { id: 'high', label: 'High' },
];

const NEPAL_TZ = 'Asia/Kathmandu';

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const int = (value) => Number(value || 0).toLocaleString();

const signedMoney = (value) => {
  const number = Number(value || 0);
  const prefix = number < 0 ? '-' : '';
  return `${prefix}${money(Math.abs(number))}`;
};

const time = (value) => {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: NEPAL_TZ,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '-';
  }
};

const sortByTimeDesc = (rows) => [...(rows || [])].sort((a, b) => {
  const left = new Date(a?.created_at || 0).getTime() || 0;
  const right = new Date(b?.created_at || 0).getTime() || 0;
  return right - left;
});

const statusClass = (group) => {
  if (group === 'pending') return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
  if (group === 'approved') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
  if (group === 'paid') return 'bg-green-500/15 text-green-300 border-green-500/30';
  if (group === 'failed' || group === 'rejected') return 'bg-red-500/15 text-red-300 border-red-500/30';
  return 'bg-white/10 text-gray-300 border-white/10';
};

const riskClass = (severity) => {
  if (severity === 'high') return 'border-red-500/40 bg-red-500/15 text-red-200';
  if (severity === 'medium') return 'border-yellow-500/40 bg-yellow-500/15 text-yellow-200';
  if (severity === 'low') return 'border-orange-500/35 bg-orange-500/10 text-orange-200';
  return 'border-white/10 bg-white/[0.03] text-gray-400';
};

export default function WithdrawalPortalPage({
  client = api,
  basePath = '/api/admin/withdrawal-portal',
}) {
  const [view, setView] = useState('dashboard');
  const [period, setPeriod] = useState('today');
  const [status, setStatus] = useState('active');
  const [tenantId, setTenantId] = useState('all');
  const [riskIndex, setRiskIndex] = useState('all');
  const [search, setSearch] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [detailActionError, setDetailActionError] = useState('');
  const [detailActionBusy, setDetailActionBusy] = useState('');

  const requestParams = useMemo(() => {
    const params = { period, status, limit: 300 };
    if (tenantId !== 'all') params.tenant_id = tenantId;
    if (riskIndex !== 'all') params.risk_index = riskIndex;
    if (search.trim()) params.search = search.trim();
    if (view === 'risky') params.risk_only = true;
    return params;
  }, [period, status, tenantId, riskIndex, search, view]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await client.get(`${basePath}/dashboard`, { params: { period } });
      setDashboard(res.data);
      setTenants(res.data?.tenants || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load withdrawal portal dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await client.get(`${basePath}/requests`, { params: requestParams });
      setRequests(res.data?.items || []);
      if ((res.data?.tenants || []).length) setTenants(res.data.tenants);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load withdrawal requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParams]);

  const openDetail = async (row) => {
    setDetailLoading(true);
    setDetail(null);
    setDetailActionError('');
    try {
      const res = await client.get(`${basePath}/request-detail`, {
        params: { request_id: row.id, kind: row.kind, source_vps: row.source_vps },
      });
      setDetail(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load withdrawal details');
    } finally {
      setDetailLoading(false);
    }
  };

  const refresh = () => {
    loadDashboard();
    loadRequests();
  };

  const applyDetailAction = async (action, values = {}) => {
    const row = detail?.request;
    if (!row || detailActionBusy) return false;
    setDetailActionBusy(action);
    setDetailActionError('');
    try {
      await client.post(`${basePath}/request-action`, {
        request_id: row.id,
        kind: row.kind,
        source_vps: row.source_vps,
        action,
        ...values,
      });
      await Promise.all([loadDashboard(), loadRequests()]);
      await openDetail(row);
      return true;
    } catch (err) {
      setDetailActionError(err?.response?.data?.detail || 'Withdrawal action could not be completed');
      return false;
    } finally {
      setDetailActionBusy('');
    }
  };

  const totals = dashboard?.totals || {};
  const tenantRows = dashboard?.tenants || [];

  return (
    <div className="space-y-5" data-testid="withdrawal-portal-page">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Wallet className="h-6 w-6 text-[#D4AF37]" />
            Withdrawal Portal
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            {dashboard?.window?.start && <span>Nepal period: {time(dashboard.window.start)} - {time(dashboard.window.end)}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-white/10 bg-[#0B0E14] p-1" data-testid="withdrawal-portal-periods">
            {PERIODS.map((item) => (
              <button
                key={item.id}
                onClick={() => setPeriod(item.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  period === item.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`withdrawal-period-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-white/10 bg-[#0B0E14] p-2 text-gray-400 transition hover:text-white"
            title="Refresh"
            data-testid="withdrawal-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading || requestsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex gap-2 rounded-xl border border-white/10 bg-[#0B0E14] p-1 w-fit">
        <button
          onClick={() => setView('dashboard')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'dashboard' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-dashboard"
        >
          <Activity className="h-4 w-4" />
          Dashboard
        </button>
        <button
          onClick={() => setView('requests')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'requests' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-requests"
        >
          <ArrowUpCircle className="h-4 w-4" />
          Withdrawal Requests
        </button>
        <button
          onClick={() => setView('risky')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'risky' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-risky"
        >
          <AlertTriangle className="h-4 w-4" />
          Risky Withdrawals
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="withdrawal-portal-error">
          {error}
        </div>
      )}

      {view === 'dashboard' ? (
        <DashboardView loading={loading} totals={totals} tenants={tenantRows} />
      ) : (
        <RequestsView
          loading={requestsLoading}
          tenants={tenants}
          tenantId={tenantId}
          setTenantId={setTenantId}
          status={status}
          setStatus={setStatus}
          riskIndex={riskIndex}
          setRiskIndex={setRiskIndex}
          search={search}
          setSearch={setSearch}
          requests={requests}
          openDetail={openDetail}
          mode={view}
        />
      )}

      <Modal
        open={detailLoading || !!detail}
        onClose={() => { if (!detailLoading) setDetail(null); }}
        title="Withdrawal Details"
        maxWidth="max-w-4xl"
      >
        {detailLoading ? (
          <div className="flex h-64 items-center justify-center"><div className="spinner" /></div>
        ) : detail ? (
          <DetailView
            detail={detail}
            onAction={applyDetailAction}
            actionBusy={detailActionBusy}
            actionError={detailActionError}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function DashboardView({ loading, totals, tenants }) {
  if (loading) return <div className="flex h-64 items-center justify-center"><div className="spinner" /></div>;
  return (
    <div className="space-y-4" data-testid="withdrawal-dashboard">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Metric icon={ArrowUpCircle} label="Total Cashouts" value={int(totals.total_count)} sub={money(totals.total_amount)} color="#fb7185" />
        <Metric icon={Activity} label="Active" value={int(totals.active_count)} sub={money(totals.active_amount)} color="#facc15" />
        <Metric icon={Clock} label="Pending" value={int(totals.pending_count)} sub={money(totals.pending_amount)} color="#fbbf24" />
        <Metric icon={ArrowDownCircle} label="Approved" value={int(totals.approved_count)} sub={money(totals.approved_amount)} color="#60a5fa" />
        <Metric icon={Wallet} label="Paid" value={int(totals.paid_count)} sub={money(totals.paid_amount)} color="#22c55e" />
      </div>

      <div className="glass-card overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          <Building2 className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-semibold text-white">Tenant Cashout Totals</h3>
          <span className="text-[10px] text-gray-500">{tenants.length} tenants</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="withdrawal-tenant-table">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Active</th>
                <th className="px-4 py-3 text-right">Pending</th>
                <th className="px-4 py-3 text-right">Approved</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {tenants.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">No tenant cashouts in this period.</td></tr>
              ) : tenants.map((tenant) => (
                <tr key={tenant.tenant_id} className="hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <TenantLogo tenant={tenant} />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{tenant.tenant_name}</p>
                        <p className="text-[11px] text-gray-500">/{tenant.tenant_slug || '-'} · {tenant.source_vps || 'local'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-white">{int(tenant.total_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-yellow-300">{int(tenant.active_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-yellow-400">{int(tenant.pending_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-300">{int(tenant.approved_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-300">{int(tenant.paid_count)}</td>
                  <td className="px-4 py-3 text-right font-black tabular-nums text-red-300">{money(tenant.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RequestsView({
  loading,
  tenants,
  tenantId,
  setTenantId,
  status,
  setStatus,
  riskIndex,
  setRiskIndex,
  search,
  setSearch,
  requests,
  openDetail,
  mode,
}) {
  const isRisky = mode === 'risky';
  return (
    <div className="space-y-4" data-testid="withdrawal-requests-portal">
      <div className="grid gap-3 lg:grid-cols-[minmax(180px,260px)_minmax(150px,220px)_minmax(150px,220px)_1fr]">
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Tenant</span>
          <select
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-tenant-filter"
          >
            <option value="all">All teams</option>
            {tenants.map((tenant) => (
              <option key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.tenant_name}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-status-filter"
          >
            {STATUSES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Risk Index</span>
          <select
            value={riskIndex}
            onChange={(event) => setRiskIndex(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-risk-index-filter"
          >
            {RISK_INDEXES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Search player</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Username, email, or name..."
              className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] pl-9 pr-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
              data-testid="withdrawal-player-search"
            />
          </div>
        </label>
      </div>

      <div className="glass-card overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          {isRisky ? <AlertTriangle className="h-4 w-4 text-yellow-300" /> : <ArrowUpCircle className="h-4 w-4 text-red-300" />}
          <h3 className="text-sm font-semibold text-white">{isRisky ? 'Risky Withdrawal Watchlist' : 'Withdrawal Request Ledger'}</h3>
          <span className="text-[10px] text-gray-500">{requests.length} shown</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="withdrawal-request-table">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Date / Time</th>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-left">Risk</th>
                <th className="px-4 py-3 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center"><div className="spinner mx-auto" /></td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">{isRisky ? 'No risky withdrawals match this filter.' : 'No withdrawal requests match this filter.'}</td></tr>
              ) : requests.map((row) => (
                <tr key={`${row.kind}-${row.source_vps}-${row.id}`} className="hover:bg-white/[0.03]">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-300">{time(row.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <TenantLogo tenant={row} className="h-8 w-8" textClassName="text-[10px]" />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{row.tenant_name}</p>
                        <p className="text-[10px] text-gray-500">{row.source_vps || 'local'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-white">{row.username}</p>
                    <p className="text-[10px] text-gray-500">{row.email || '-'}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    <p>{row.payment_method || 'Manual'}</p>
                    {row.details_preview && <p className="max-w-[220px] truncate text-[10px] text-gray-500">{row.details_preview}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-black tabular-nums text-red-300">{money(row.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${statusClass(row.status_group)}`}>
                      {row.status_group || row.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <RiskBadge risk={row.risk} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-gray-200 transition hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                      data-testid={`withdrawal-detail-${row.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RiskBadge({ risk }) {
  const flags = risk?.flags || [];
  const severity = risk?.severity || 'clear';
  if (!flags.length) {
    return (
      <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${riskClass(severity)}`}>
        Clear
      </span>
    );
  }
  return (
    <div className="min-w-[170px] space-y-1">
      <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black uppercase ${riskClass(severity)}`}>
        {severity} · {Number(risk.score || 0)}%
      </span>
      <div className="flex max-w-[240px] flex-wrap gap-1">
        {flags.slice(0, 2).map((flag) => (
          <span key={flag.code} className="rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[10px] text-gray-300">
            {flag.label}
          </span>
        ))}
        {flags.length > 2 && <span className="text-[10px] text-gray-500">+{flags.length - 2}</span>}
      </div>
    </div>
  );
}

function DetailView({ detail, onAction, actionBusy, actionError }) {
  const player = detail?.player || {};
  const request = detail?.request || {};
  const risk = request.risk || {};
  const totals = player.totals || {};
  const [activeLedger, setActiveLedger] = useState('loads');
  const referralSource = player.referral_source || {};
  const referredName = referralSource.username || referralSource.full_name || player.referred_by?.username || player.referred_by?.full_name || player.referred_by_staff?.username || player.referred_by_staff?.full_name || '-';
  const referredEmail = referralSource.email || player.referred_by?.email || player.referred_by_staff?.email || '-';
  const referralCode = referralSource.source_code || referralSource.referral_code || player.referred_by?.referral_code || player.referred_by_staff?.referral_code || '-';
  const walletDeposits = player.wallet_deposits || [];
  const walletCashouts = player.wallet_cashouts || [];
  const referralRows = [
    ...(player.referral_earnings || []),
    ...(player.referred_players || []).map((row) => ({
      ...row,
      kind: 'referred_player',
      label: 'Referred player',
      method: row.email || row.full_name || '',
      reference: row.source_code || '',
      amount: null,
    })),
  ];
  const revenueRows = sortByTimeDesc([
    ...walletDeposits.map((row) => ({ ...row, label: row.label || 'Wallet topup', amount: Number(row.amount || 0), tone: 'in' })),
    ...walletCashouts.map((row) => ({ ...row, label: row.label || 'Wallet withdrawal', amount: -Math.abs(Number(row.amount || 0)), tone: 'out' })),
  ]);
  const ledgers = [
    {
      id: 'loads',
      icon: ArrowDownCircle,
      label: 'Total Load',
      value: money(totals.total_load),
      sub: `${int(totals.total_load_count)} game loads`,
      color: '#60a5fa',
      rows: player.last_load_games || [],
      empty: 'No game load activity',
    },
    {
      id: 'redeems',
      icon: ArrowUpCircle,
      label: 'Total Redeemed',
      value: money(totals.total_redeemed),
      sub: `${int(totals.total_redeem_count)} game redeems`,
      color: '#22c55e',
      rows: player.last_withdrawal_from_games || [],
      empty: 'No game redeem activity',
    },
    {
      id: 'wallet-deposits',
      icon: Wallet,
      label: 'Wallet Topups',
      value: money(totals.wallet_topups ?? totals.wallet_deposited),
      sub: `${int(totals.wallet_deposit_count)} topups`,
      color: '#D4AF37',
      rows: walletDeposits,
      empty: 'No wallet topups',
    },
    {
      id: 'wallet-cashouts',
      icon: ArrowUpCircle,
      label: 'Wallet Withdrawals',
      value: money(totals.wallet_cashout),
      sub: `${int(totals.wallet_cashout_count)} withdrawals`,
      color: '#fb7185',
      rows: walletCashouts,
      empty: 'No wallet withdrawals',
    },
    {
      id: 'revenue',
      icon: Activity,
      label: 'Revenue',
      value: money(totals.revenue),
      sub: 'topups - withdrawals',
      color: Number(totals.revenue || 0) >= 0 ? '#22c55e' : '#fb7185',
      rows: revenueRows,
      empty: 'No revenue activity',
    },
    {
      id: 'referrals',
      icon: Users,
      label: 'Referral Earned',
      value: money(totals.total_earned_by_referral),
      sub: `${int(totals.total_referrals)} referrals`,
      color: '#a78bfa',
      rows: referralRows,
      empty: 'No referral activity',
    },
  ];
  const selectedLedger = ledgers.find((item) => item.id === activeLedger) || ledgers[0];

  return (
    <div className="space-y-4" data-testid="withdrawal-detail-modal">
      <div className="grid gap-3 md:grid-cols-3">
        <Info label="Player" value={player.username || request.username || '-'} sub={player.email || request.email || ''} icon={UserRound} />
        <Info label="Tenant" value={player.tenant_name || request.tenant_name || '-'} sub={player.tenant_slug ? `/${player.tenant_slug}` : ''} icon={Building2} />
        <Info label="Request" value={money(request.amount)} sub={`${request.status || '-'} · ${time(request.created_at)}`} icon={ArrowUpCircle} />
      </div>

      <RiskReview risk={risk} />

      <WithdrawalActions
        request={request}
        onAction={onAction}
        actionBusy={actionBusy}
        actionError={actionError}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {ledgers.map((item) => (
          <Metric
            key={item.id}
            icon={item.icon}
            label={item.label}
            value={item.value}
            sub={item.sub}
            color={item.color}
            active={activeLedger === item.id}
            onClick={() => setActiveLedger(item.id)}
          />
        ))}
      </div>

      <DetailLedger title={selectedLedger.label} rows={selectedLedger.rows} empty={selectedLedger.empty} signed={selectedLedger.id === 'revenue'} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Timeline title="Last Load Games" rows={player.last_load_games || []} empty="No recent game loads" />
        <Timeline title="Last Withdrawal From Games" rows={player.last_withdrawal_from_games || []} empty="No recent game redeems" />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Player Profile</p>
          <dl className="mt-3 space-y-2 text-sm">
            <Line label="Join date" value={time(player.join_date)} />
            <Line label="Phone" value={player.phone || '-'} />
            <Line label="Last load amounts" value={(player.last_load_amounts || []).length ? player.last_load_amounts.map(money).join(', ') : '-'} />
          </dl>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Referral Source</p>
          <dl className="mt-3 space-y-2 text-sm">
            <Line label="Source" value={referralSource.label || '-'} />
            <Line label="Referred name" value={referredName} />
            <Line label="Referred email" value={referredEmail} />
            <Line label="Referral code" value={referralCode} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function WithdrawalActions({ request, onAction, actionBusy, actionError }) {
  const [amount, setAmount] = useState(String(Number(request?.amount || 0).toFixed(2)));
  const [reason, setReason] = useState('');
  useEffect(() => {
    setAmount(String(Number(request?.amount || 0).toFixed(2)));
  }, [request?.id, request?.amount]);
  const pending = request?.kind === 'payment_request' && request?.status_group === 'pending';
  const approved = request?.kind === 'payment_request' && request?.status_group === 'approved';
  const busy = Boolean(actionBusy);
  const amountChanged = Math.abs(Number(amount || 0) - Number(request?.amount || 0)) >= 0.005;

  if (request?.kind !== 'payment_request') {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4 text-xs text-gray-500">
        This payout record is read-only here. Canonical payment-request withdrawals can be approved, declined, or corrected from this portal.
      </div>
    );
  }

  if (!pending && !approved) return null;

  const saveAmount = async () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    await onAction('edit_amount', { amount: parsed });
  };

  const decline = async () => {
    if (!reason.trim()) return;
    await onAction('reject', { reason: reason.trim() });
  };

  const reverse = async () => {
    if (!window.confirm('Return this approved withdrawal to pending review? It will not create a wallet ledger entry.')) return;
    await onAction('reverse_to_pending');
  };

  return (
    <section className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-4" data-testid="withdrawal-action-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-white">Withdrawal decision</p>
          <p className="mt-1 text-xs text-gray-400">
            {pending
              ? 'Adjust the held amount, then approve or decline with a reason. The linked Telegram approval card stays synchronized.'
              : 'This approval can be returned to pending only before a linked payout is paid or in progress.'}
          </p>
        </div>
        <span className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${statusClass(request.status_group)}`}>
          {request.status_group}
        </span>
      </div>

      {actionError && (
        <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="withdrawal-action-error">
          {actionError}
        </p>
      )}

      {pending ? (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Payout amount</span>
            <div className="flex gap-2">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={busy}
                className="h-10 min-w-0 flex-1 rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60 disabled:opacity-50"
                data-testid="withdrawal-edit-amount"
              />
              <button
                type="button"
                onClick={saveAmount}
                disabled={busy || Number(amount) <= 0}
                className="rounded-lg border border-[#D4AF37]/50 px-3 text-xs font-bold text-[#D4AF37] transition hover:bg-[#D4AF37]/10 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="withdrawal-save-amount"
              >
                {actionBusy === 'edit_amount' ? 'Saving…' : 'Save amount'}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Decline reason</span>
            <textarea
              rows="2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={busy}
              placeholder="Required if declining"
              className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2 text-sm text-white outline-none placeholder:text-gray-600 focus:border-[#D4AF37]/60 disabled:opacity-50"
              data-testid="withdrawal-decline-reason"
            />
          </label>
          <div className="flex flex-wrap gap-2 lg:col-span-2">
            <button
              type="button"
              onClick={() => onAction('approve')}
              disabled={busy || amountChanged}
              className="rounded-lg bg-emerald-500 px-4 py-2 text-xs font-black text-emerald-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="withdrawal-approve"
            >
              {actionBusy === 'approve' ? 'Approving…' : amountChanged ? 'Save amount before approving' : `Approve ${money(request.amount)}`}
            </button>
            <button
              type="button"
              onClick={decline}
              disabled={busy || !reason.trim()}
              className="rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-2 text-xs font-black text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="withdrawal-decline"
            >
              {actionBusy === 'reject' ? 'Declining…' : 'Decline with reason'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={reverse}
            disabled={busy}
            className="rounded-lg border border-yellow-400/50 bg-yellow-400/10 px-4 py-2 text-xs font-black text-yellow-100 transition hover:bg-yellow-400/20 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="withdrawal-reverse-pending"
          >
            {actionBusy === 'reverse_to_pending' ? 'Reversing…' : 'Reverse to pending'}
          </button>
          <p className="text-xs text-gray-500">No wallet ledger entry is written by this reversal.</p>
        </div>
      )}
    </section>
  );
}

function RiskReview({ risk }) {
  const flags = risk?.flags || [];
  const metrics = risk?.metrics || {};
  if (!flags.length) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-gray-500" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Risk Review</p>
          <span className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${riskClass('clear')}`}>Clear</span>
        </div>
        <p className="mt-2 text-xs text-gray-500">No suspicious withdrawal signals found for this request.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/[0.06] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-300" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-yellow-100/70">Risk Review</p>
        <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase ${riskClass(risk.severity)}`}>
          {risk.severity || 'risk'} · {Number(risk.score || 0)}%
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {flags.map((flag) => (
          <span key={flag.code} className="rounded-lg border border-white/10 bg-[#0B0E14]/70 px-2 py-1 text-xs text-gray-200">
            {flag.label}
          </span>
        ))}
      </div>
      <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Line label="Wallet topups" value={money(metrics.wallet_topups)} />
        <Line label="Cashout exposure" value={money(metrics.cashout_exposure)} />
        <Line label="Projected revenue" value={signedMoney(metrics.projected_revenue)} />
        <Line label="Game activity" value={`${money(metrics.total_game_load)} / ${money(metrics.total_game_redeemed)}`} />
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, color, onClick, active = false }) {
  const className = `rounded-xl border p-4 text-left transition ${
    active
      ? 'border-[#D4AF37]/60 bg-[#1A2030] shadow-[0_0_0_1px_rgba(212,175,55,0.16)]'
      : 'border-white/10 bg-[#131822]'
  } ${onClick ? 'cursor-pointer hover:border-[#D4AF37]/45 hover:bg-[#161C28]' : ''}`;
  const content = (
    <>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="mt-1 text-[11px] text-gray-500">{sub}</p>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }
  return (
    <div className={className}>
      {content}
    </div>
  );
}

function Info({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#D4AF37]" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</p>
      </div>
      <p className="truncate text-lg font-bold text-white">{value}</p>
      {sub && <p className="truncate text-[11px] text-gray-500">{sub}</p>}
    </div>
  );
}

function Timeline({ title, rows, empty }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{title}</p>
      {rows.length === 0 ? (
        <p className="py-5 text-center text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div key={`${row.id || index}-${row.created_at}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{row.game || row.provider || 'Unknown game'}</p>
                <p className="text-[10px] text-gray-500">{row.provider || '-'} · {time(row.created_at)}</p>
              </div>
              <p className="shrink-0 font-black tabular-nums text-[#D4AF37]">{money(row.amount)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailLedger({ title, rows, empty, signed = false }) {
  const sortedRows = sortByTimeDesc(rows || []);
  return (
    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#0B0E14] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{title} Details</p>
        <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-semibold text-gray-400">{sortedRows.length} rows</span>
      </div>
      {sortedRows.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="max-h-[330px] overflow-auto rounded-lg border border-white/5">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#101620] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Date / Time</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Detail</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sortedRows.map((row, index) => {
                const hasAmount = row.amount !== null && row.amount !== undefined && row.amount !== '';
                const amount = Number(row.amount || 0);
                const source = row.game || row.label || row.provider || row.username || row.method || 'Activity';
                const detailBits = [
                  row.provider && row.provider !== source ? row.provider : null,
                  row.method && row.method !== source ? row.method : null,
                  row.status,
                  row.reference,
                ].filter(Boolean);
                return (
                  <tr key={`${row.id || row.reference || index}-${row.created_at || index}`} className="hover:bg-white/[0.03]">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-300">{time(row.created_at)}</td>
                    <td className="max-w-[220px] px-3 py-2">
                      <p className="truncate font-semibold text-white">{source}</p>
                    </td>
                    <td className="max-w-[260px] px-3 py-2 text-xs text-gray-500">
                      <p className="truncate">{detailBits.length ? detailBits.join(' · ') : '-'}</p>
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 text-right font-black tabular-nums ${
                      signed && amount < 0 ? 'text-red-300' : 'text-[#D4AF37]'
                    }`}>
                      {hasAmount ? (signed ? signedMoney(amount) : money(amount)) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="truncate text-right font-semibold text-gray-100">{value}</dd>
    </div>
  );
}
